import React from 'react';
import {useSelector} from "react-redux";
import _ from 'lodash';
import {Activity, AlertCircle, CheckCircle2, Globe, TrendingUp, Users, XCircle} from "lucide-react";

export default function DashboardStats() {
    const sites = useSelector(state => state.sites.items);

    // Vérification de sécurité avec lodash
    if (!_.isArray(sites) || sites.length === 0) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {[1, 2, 3, 4].map((item) => (
                    <div key={item} className="bg-white rounded-xl shadow p-6 animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
                        <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                    </div>
                ))}
            </div>
        );
    }

    // Calcul des statistiques avec lodash
    const totalSites = sites.length;
    const activeSites = _.filter(sites, site => _.get(site, 'status') === 'active').length;
    const offlineSites = _.filter(sites, site =>
        _.get(site, 'status') === 'inactive' || _.get(site, 'status') === 'error'
    ).length;

    const totalVisitorsToday = _.sumBy(sites, site => _.toNumber(_.get(site, 'todayVisites', 0)));
    const totalVisitorsAllTime = _.sumBy(sites, site => _.toNumber(_.get(site, 'totalVisites', 0)));

    // Trouver les sites avec le plus de visiteurs aujourd'hui
    const trendingSite = _.maxBy(sites, site => _.toNumber(_.get(site, 'todayVisites', 0)));

    const stats = [
        {
            title: "Sites Actifs",
            value: activeSites,
            total: totalSites,
            icon: Globe,
            color: "text-blue-600",
            bgColor: "bg-blue-50",
            description: `${activeSites} sur ${totalSites} sites`,
            trend: activeSites === totalSites ? "Tous les sites" : `${offlineSites} hors ligne`
        },
        {
            title: "Visiteurs Aujourd'hui",
            value: totalVisitorsToday.toLocaleString(),
            icon: Users,
            color: "text-green-600",
            bgColor: "bg-green-50",
            description: "Visiteurs uniques",
            trend: trendingSite ? `Top: ${_.get(trendingSite, 'name', 'Unknown')}` : "Aucune donnée"
        },
        {
            title: "Performance",
            value: `${totalSites > 0 ? Math.round((activeSites / totalSites) * 100) : 0}%`,
            icon: Activity,
            color: "text-purple-600",
            bgColor: "bg-purple-50",
            description: "Taux de disponibilité",
            status: totalSites === activeSites ?
                { text: "Excellent", icon: CheckCircle2, color: "text-green-500" } :
                { text: "À surveiller", icon: AlertCircle, color: "text-yellow-500" }
        },
        {
            title: "Évolution Hebdo",
            value: "+12.5%",
            icon: TrendingUp,
            color: "text-orange-600",
            bgColor: "bg-orange-50",
            description: "Vs semaine dernière",
            trend: totalVisitorsToday > 0 ? "En hausse" : "Stable"
        }
    ];

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {stats.map((stat, index) => (
                <div
                    key={index}
                    className="bg-white rounded-xl shadow hover:shadow-lg transition-shadow duration-300 p-6"
                >
                    <div className="flex items-center justify-between mb-4">
                        <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                            <stat.icon className={`h-6 w-6 ${stat.color}`} />
                        </div>
                        <div className="text-right">
              <span className="text-2xl font-bold text-gray-900">
                {stat.value}
              </span>
                            {stat.total && (
                                <span className="text-sm text-gray-500 ml-1">
                  /{stat.total}
                </span>
                            )}
                        </div>
                    </div>

                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {stat.title}
                    </h3>

                    <p className="text-sm text-gray-600 mb-1">
                        {stat.description}
                    </p>

                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                        {stat.status ? (
                            <div className={`flex items-center text-sm ${stat.status.color}`}>
                                <stat.status.icon className="h-4 w-4 mr-1" />
                                <span>{stat.status.text}</span>
                            </div>
                        ) : (
                            <div className="text-sm text-gray-500">
                                {stat.trend}
                            </div>
                        )}

                        {stat.title === "Sites Actifs" && offlineSites > 0 && (
                            <div className="flex items-center text-sm text-red-500">
                                <XCircle className="h-4 w-4 mr-1" />
                                <span>{offlineSites} hors ligne</span>
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}
